/* eslint-disable max-lines */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { AxiosError } from 'axios';

import { useThemeContext } from '@utils/hooks/use-theme-context';
import { useGetTokens } from '@utils/api/tokens/use-get-tokens.tsx';
import { useCreateToken } from '@utils/api/tokens/use-create-token.tsx';
import useDeleteToken from '@utils/api/tokens/use-delete-token.tsx';
import { useOrganization } from '@utils/api/users/use-get-organization.tsx';
import { useGetMe } from '@utils/api/users/use-get-me.tsx';
import { useNewAppSearchParams } from '@utils/hooks/use-new-app-search-params.ts';

import Button from '@components/atoms/ui-kit/button.tsx';
import Modal from '@components/atoms/modals/modal.tsx';
import Input from '@components/atoms/ui-kit/input.tsx';
import { usePageHeader } from '@utils/hooks/use-page-header';
import ConfirmationModal from '@components/atoms/modals/confirmation-modal.tsx';
import RadioGroup from '@components/atoms/ui-kit/raido-group.tsx';
import DataGridV2, {
  type DataGridColumn
} from '@components/atoms/datatable/data-grid-v2';
import type { GridAction } from '@components/atoms/datatable/grid-action-controller.tsx';
import { Badge } from '@components/atoms/ui-kit/badge.tsx';
import { Card } from '@components/atoms/ui-kit/card.tsx';
import { Text } from '@components/atoms/ui-kit/text.tsx';

import { ResellerToken } from '@utils/types/resellers/reseller-token.ts';
import { PATHS } from '../../router/paths.ts';
import {
  Check,
  ClipboardCopy,
  Info,
  MessageCircleWarning,
  Plus,
  Trash2
} from 'lucide-react';

function tokenTypeBadgeColor(
  type: string
): 'blue' | 'amber' | 'green' | 'zinc' {
  if (type === 'realtime') return 'blue';
  if (type === 'reseller') return 'amber';
  return 'green';
}

export default function ApiKeys() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getColour, theme } = useThemeContext();
  const isDark = theme === 'dark';

  const [isCreating, setIsCreating] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tokenName, setTokenName] = useState('');
  const [createdToken, setCreatedToken] = useState<ResellerToken | null>(null);
  const [tokenCopied, setTokenCopied] = useState(false);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState<{
    isOpen: boolean;
    tokenId: number | null;
    tokenName: string;
  }>({ isOpen: false, tokenId: null, tokenName: '' });

  const { searchParams } = useNewAppSearchParams();
  const qString = (searchParams.q as string | undefined) || undefined;

  const { data: organization } = useOrganization();

  const types = useMemo(
    () => [
      { name: 'batch', description: t('store-the-data-then-process-it') },
      { name: 'realtime', description: t('process-data-as-it-comes') },
      ...(organization?.is_reseller
        ? [{ name: 'reseller', description: t('access-the-reseller-api') }]
        : [])
    ],
    [organization?.is_reseller, t]
  );

  const years_valid = useMemo(
    () => [
      { id: 1, name: t('1-year'), description: t('recommended') },
      { id: 2, name: t('2-years'), description: '' },
      { id: 5, name: t('5-years'), description: '' }
    ],
    [t]
  );

  const [tokenType, setTokenType] = useState(types[0]);
  const [tokenExpires, setTokenExpires] = useState(1);

  const createToken = useCreateToken();
  const { data: tokens, isLoading, isFetching, error } = useGetTokens();
  const { data: user } = useGetMe();
  const deleteTokenMutation = useDeleteToken();

  useEffect(() => {
    if (error && (error as AxiosError)?.response?.status === 403) {
      navigate(PATHS().HOME);
    }
  }, [error, navigate]);

  const handleCreateToken = () => {
    if (!tokenName.trim()) {
      toast.error(t('token-name-required'));
      return;
    }

    setIsCreating(true);
    const name = tokenName.trim();
    const type = tokenType.name.toLowerCase();

    createToken
      .mutateAsync({ name, type, years_valid: tokenExpires })
      .then(data => {
        const tokenData = data?.result || data;
        setCreatedToken(tokenData);
        setIsModalOpen(false);
        setTokenName('');
        setTokenCopied(false);
        toast.success(t('token-created-successfully'));
      })
      .catch((err: unknown) => {
        const fallbackMessage = t('an-error-occurred-while-creating-the-token');
        const errData = (
          err as AxiosError<{
            detail?: unknown[];
            message?: string;
            error_description?: string;
          }>
        )?.response?.data;

        let messages: string[] = [];
        if (Array.isArray(errData?.detail)) {
          type ValidationErr = { loc?: string[]; msg?: string };
          messages = (errData.detail as ValidationErr[]).map(e => {
            const field = e.loc?.[1] ?? 'field';
            return `${field}: ${e.msg}`;
          });
        } else if (errData?.message) {
          messages = [errData.message];
        } else if (errData?.error_description) {
          messages = [errData.error_description];
        } else {
          messages = [fallbackMessage];
        }

        toast.error(messages.join('\n'));
      })
      .finally(() => {
        setIsCreating(false);
      });
  };

  const handleCopyToken = useCallback(
    (token: string) => {
      try {
        navigator.clipboard.writeText(token).catch(() => {
          toast.error(t('failed-to-copy-token-to-clipboard'));
        });
        setTokenCopied(true);
        toast.success(t('token-copied-to-clipboard'));
        setTimeout(() => setTokenCopied(false), 3000);
      } catch {
        toast.error(t('failed-to-copy-token-to-clipboard'));
      }
    },
    [t]
  );

  const handleDeleteToken = () => {
    if (!deleteConfirmModal.tokenId) return;

    const loadingToastId = toast.loading(t('deleting-token'));
    deleteTokenMutation
      .mutateAsync(String(deleteConfirmModal.tokenId))
      .then(() => {
        toast.dismiss(loadingToastId);
        toast.success(t('token-deleted-successfully'));
        setDeleteConfirmModal({ isOpen: false, tokenId: null, tokenName: '' });
      })
      .catch((err: unknown) => {
        toast.dismiss(loadingToastId);
        const fallbackMessage = t('an-error-has-occurred');
        const errData = (
          err as AxiosError<{
            detail?: unknown[];
            message?: string;
            error_description?: string;
          }>
        )?.response?.data;

        let messages: string[] = [];
        if (Array.isArray(errData?.detail)) {
          type ValidationErr = { loc?: string[]; msg?: string };
          messages = (errData.detail as ValidationErr[]).map(e => {
            const field = e.loc?.[1] ?? 'field';
            return `${field}: ${e.msg}`;
          });
        } else if (errData?.message) {
          messages = [errData.message];
        } else if (errData?.error_description) {
          messages = [errData.error_description];
        } else {
          messages = [fallbackMessage];
        }

        toast.error(messages.join('\n'));
      });
  };

  const rows = useMemo(() => {
    if (!tokens || !Array.isArray(tokens.result)) return [];
    return tokens.result.map((token: ResellerToken) => ({
      id: token.id || 0,
      key: token.key || '',
      token_value: token.token_value || '',
      name: token.name || '',
      type: token.type || '',
      expires: token.expires || ''
    }));
  }, [tokens]);

  const roleValues = (user?.result?.roles ?? []).map(role => role.value);
  const canDeleteToken =
    roleValues.includes('admin') || roleValues.includes('developer');

  const columns = useMemo<DataGridColumn<(typeof rows)[number]>[]>(
    () => [
      {
        id: 'id',
        accessorKey: 'id',
        header: t('id'),
        maxWidth: 80,
        enableSorting: true,
        enableFiltering: false,
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        id: 'name',
        accessorKey: 'name',
        header: t('name'),
        minWidth: 200,
        enableSorting: false,
        enableFiltering: true,
        filterVariant: 'text',
        filterKey: 'name',
        serverOperator: 'ilike',
        enableRowCopyOnClick: false,
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        id: 'key',
        accessorKey: 'key',
        header: t('key'),
        minWidth: 350,
        enableSorting: false,
        enableFiltering: false,
        enableRowCopyOnClick: false,
        cell: ({ row }) => {
          const key = row.original.key;
          const tokenValue = row.original.token_value;
          return (
            <div className="flex items-center gap-2">
              <code className="px-2 py-0.5 rounded-2xl font-mono bg-app-soft border border-hairline">
                <Text as="span" size="xs">
                  {key}
                </Text>
              </code>
              <button
                type="button"
                className="flex-shrink-0 transition-opacity hover:opacity-60"
                style={{ color: getColour('icon') }}
                onClick={() => handleCopyToken(tokenValue)}
                aria-label={t('copy')}
              >
                <ClipboardCopy className="h-4 w-4" />
              </button>
            </div>
          );
        }
      },
      {
        id: 'type',
        accessorKey: 'type',
        header: t('type'),
        maxWidth: 120,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'select',
        filterKey: 'type',
        serverOperator: 'eq',
        filterOptions: [
          { label: 'BATCH', value: 'batch' },
          { label: 'REALTIME', value: 'realtime' },
          { label: 'RESELLER', value: 'reseller' },
          { label: 'SHARED', value: 'shared' }
        ],
        cell: ({ getValue }) => {
          const type = String(getValue() ?? '');
          return (
            <Badge color={tokenTypeBadgeColor(type)}>
              {type.toUpperCase()}
            </Badge>
          );
        }
      },
      {
        id: 'expires',
        accessorKey: 'expires',
        header: t('expires'),
        maxWidth: 160,
        enableSorting: false,
        enableFiltering: false,
        cell: ({ getValue }) => String(getValue() ?? '')
      }
    ],
    [t, getColour, handleCopyToken]
  );

  const actions = useMemo<GridAction<(typeof rows)[number]>[]>(
    () =>
      !canDeleteToken
        ? []
        : [
            {
              id: 'delete',
              label: t('delete'),
              icon: <Trash2 className="h-4 w-4" />,
              intent: 'danger',
              run: row => {
                setDeleteConfirmModal({
                  isOpen: true,
                  tokenId: Number(row.id),
                  tokenName: row.name
                });
              }
            }
          ],
    [canDeleteToken, t]
  );

  const FORM_ID = 'create-token-form';

  const typeOptions = useMemo(
    () =>
      types.map(tp => ({
        value: tp.name.toLowerCase(),
        label: tp.name.toUpperCase(),
        description: `(${tp.description})`
      })),
    [types]
  );

  const expiresOptions = useMemo(
    () =>
      years_valid.map(y => ({
        value: String(y.id),
        label: y.name,
        description: y.description
      })),
    [years_valid]
  );

  const dismissCreatedToken = () => {
    setCreatedToken(null);
    setTokenCopied(false);
  };

  // Semantic alert colors — stable across themes
  const greenColors = {
    border: 'rgba(34,197,94,0.30)',
    icon: isDark ? 'rgb(74,222,128)' : 'rgb(22,163,74)',
    title: isDark ? 'rgb(134,239,172)' : 'rgb(21,128,61)'
  };
  const blueColors = {
    bg: isDark ? 'rgba(59,130,246,0.10)' : 'rgba(59,130,246,0.06)',
    border: 'rgba(59,130,246,0.22)',
    icon: isDark ? 'rgb(147,197,253)' : 'rgb(37,99,235)',
    title: isDark ? 'rgb(186,230,253)' : 'rgb(30,64,175)',
    text: isDark ? 'rgb(147,197,253)' : 'rgb(29,78,216)'
  };
  const orangeColors = {
    bg: isDark ? 'rgba(249,115,22,0.10)' : 'rgba(249,115,22,0.06)',
    border: 'rgba(249,115,22,0.22)',
    icon: isDark ? 'rgb(253,186,116)' : 'rgb(234,88,12)',
    title: isDark ? 'rgb(254,215,170)' : 'rgb(154,52,18)',
    text: isDark ? 'rgb(253,186,116)' : 'rgb(194,65,12)'
  };
  const yellowColors = {
    bg: isDark ? 'rgba(234,179,8,0.10)' : 'rgba(234,179,8,0.06)',
    border: 'rgba(234,179,8,0.22)',
    text: isDark ? 'rgb(253,224,71)' : 'rgb(161,98,7)'
  };

  usePageHeader({
    title: t('api-keys'),
    description: t('create-new-api-token-caption'),
    actions: (
      <Button
        label={t('new-token')}
        icon={<Plus width={16} />}
        onClick={() => setIsModalOpen(true)}
        variant="primary"
      />
    )
  });

  return (
    <div className="bg-app min-h-screen">
      {/* Create key modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setTokenName('');
        }}
        size="medium"
        title={t('create-new-api-key')}
        footerActions={[
          <Button
            key="cancel"
            type="button"
            onClick={() => {
              setIsModalOpen(false);
              setTokenName('');
            }}
            label={t('cancel')}
            size="small"
            disabled={isCreating}
            variant="smoke"
          />,
          <Button
            key="create"
            type="submit"
            form={FORM_ID}
            label={isCreating ? t('creating') : t('create')}
            size="small"
            disabled={isCreating || !tokenName.trim()}
            variant="primary"
          />
        ]}
      >
        <form
          id={FORM_ID}
          className="px-2"
          onSubmit={e => {
            e.preventDefault();
            if (!isCreating) handleCreateToken();
          }}
        >
          <button type="submit" className="hidden" aria-hidden="true" />

          <div>
            <Text
              as="p"
              size="sm"
              className="pb-4 mb-4 border-b border-hairline"
            >
              {t('token-generation-description')}
            </Text>

            <Input
              id="name"
              label={t('name')}
              placeholder={t('enter-token-name')}
              value={tokenName}
              onChange={e => setTokenName(e.target.value)}
              type="text"
              autoComplete="off"
              required
            />

            <div className="py-4 border-b border-hairline">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <Text
                    as="h2"
                    size="sm"
                    weight="medium"
                    className="leading-6 pb-2"
                  >
                    {t('type')} *
                  </Text>
                  <RadioGroup
                    name="token_type"
                    options={typeOptions}
                    value={tokenType.name.toLowerCase()}
                    onChange={v =>
                      setTokenType(
                        types.find(tp => tp.name.toLowerCase() === v) ??
                          types[0]
                      )
                    }
                    size="small"
                    wrapperClass="w-full"
                  />
                </div>
                <div>
                  <Text
                    as="h2"
                    size="sm"
                    weight="medium"
                    className="leading-6 pb-2"
                  >
                    {t('expires')} *
                  </Text>
                  <RadioGroup
                    name="token_expires"
                    options={expiresOptions}
                    value={String(tokenExpires)}
                    onChange={v => setTokenExpires(Number(v))}
                    size="small"
                    wrapperClass="w-full"
                  />
                </div>
              </div>
            </div>

            {tokenType.name.toLowerCase() === 'realtime' && (
              <div
                className="mt-4 p-3 rounded-2xl"
                style={{
                  backgroundColor: blueColors.bg,
                  border: `1px solid ${blueColors.border}`
                }}
              >
                <div className="flex items-start gap-2">
                  <Info
                    className="h-5 w-5 flex-shrink-0 mt-0.5"
                    style={{ color: blueColors.icon }}
                  />
                  <div>
                    <Text
                      as="p"
                      weight="semibold"
                      className="mb-1"
                      style={{ color: blueColors.title }}
                    >
                      {t('realtime-token-info-title')}
                    </Text>
                    <Text as="p" size="xs" style={{ color: blueColors.text }}>
                      {t('realtime-token-info-desc')}
                    </Text>
                  </div>
                </div>
              </div>
            )}

            <Text
              as="p"
              size="xs"
              className="mt-6 pt-2 flex items-center gap-2 border-t border-hairline"
              style={{ color: yellowColors.text }}
            >
              <MessageCircleWarning width={20} />
              {t('api-token-validity')}
            </Text>
          </div>
        </form>
      </Modal>

      {/* Delete confirmation modal */}
      <ConfirmationModal
        isOpen={deleteConfirmModal.isOpen}
        onClose={() =>
          setDeleteConfirmModal({ isOpen: false, tokenId: null, tokenName: '' })
        }
        onConfirm={handleDeleteToken}
        size="small"
        title={t('delete-token')}
        caption={`${t('delete-token-confirmation')} "${deleteConfirmModal.tokenName}"?`}
        confirmLabel={t('delete')}
        cancelLabel={t('cancel')}
        destructive
        autoFocusConfirm
      />

      <Text as="p" size="sm" className="px-2 py-2">
        {t('see-documentation')}&nbsp;
        <Link to={'https://docs.scriptix.io/'} target={'_blank'}>
          <Text
            as="span"
            weight="semibold"
            tone="brand"
            className="hover:underline"
          >
            {t('here')}.
          </Text>
        </Link>
      </Text>

      {/* Reveal-once token banner */}
      {createdToken && (
        <div className="mb-2 px-4 md:px-0">
          <Card
            padding="none"
            className="p-3 shadow-card space-y-2"
            style={{ borderColor: greenColors.border }}
          >
            {/* Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <Check
                  className="h-4 w-4 shrink-0"
                  style={{ color: greenColors.icon }}
                />
                <Text
                  as="span"
                  size="sm"
                  weight="semibold"
                  style={{ color: greenColors.title }}
                >
                  {t('token-created-successfully')}
                </Text>
              </div>
              <button
                onClick={dismissCreatedToken}
                className="leading-none p-0.5 transition-opacity hover:opacity-60"
                style={{ color: getColour('inactiveText') }}
                aria-label={t('close')}
              >
                ×
              </button>
            </div>

            {/* Name + Type inline */}
            <div className="flex items-center gap-0">
              <div className="flex items-center gap-1.5 pr-3">
                <Text as="span" size="xs" weight="medium" tone="muted">
                  {t('token-name')}
                </Text>
                <Text
                  as="span"
                  size="xs"
                  weight="semibold"
                  className="font-mono"
                >
                  {createdToken.name}
                </Text>
              </div>
              <div
                className="w-px h-3.5 mx-0 shrink-0"
                style={{ backgroundColor: getColour('hairline') }}
              />
              <div className="flex items-center gap-1.5 pl-3">
                <Text as="span" size="xs" weight="medium" tone="muted">
                  {t('token-type')}
                </Text>
                <Badge color={tokenTypeBadgeColor(createdToken.type)}>
                  {createdToken.type.toUpperCase()}
                </Badge>
              </div>
            </div>

            {/* Token key (reveal-once) */}
            <div
              className="flex items-center gap-2 px-3 py-2 rounded-2xl"
              style={{
                backgroundColor: yellowColors.bg,
                border: `1px solid ${yellowColors.border}`
              }}
            >
              <code className="flex-1 font-mono break-all select-all">
                <Text as="span" size="xs">
                  {createdToken.key}
                </Text>
              </code>
              <Button
                onClick={() => handleCopyToken(createdToken.key)}
                icon={
                  tokenCopied ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <ClipboardCopy className="h-4 w-4" />
                  )
                }
                label={tokenCopied ? t('copied') : t('copy')}
                size="small"
                variant={tokenCopied ? 'smoke' : 'success'}
              />
            </div>

            {/* Security warning */}
            <div
              className="flex items-start gap-2 px-3 py-2 rounded-2xl"
              style={{
                backgroundColor: orangeColors.bg,
                border: `1px solid ${orangeColors.border}`
              }}
            >
              <MessageCircleWarning
                className="h-4 w-4 shrink-0 mt-0.5"
                style={{ color: orangeColors.icon }}
              />
              <div>
                <Text
                  as="span"
                  size="xs"
                  weight="semibold"
                  style={{ color: orangeColors.title }}
                >
                  {t('important-security-notice')}
                </Text>
                <Text
                  as="span"
                  size="xs"
                  className="ml-1"
                  style={{ color: orangeColors.text }}
                >
                  — {t('token-shown-once-warning')}
                </Text>
              </div>
            </div>

            {/* Realtime usage note */}
            {createdToken.type === 'realtime' && (
              <div
                className="flex items-start gap-2 px-3 py-2 rounded-2xl"
                style={{
                  backgroundColor: blueColors.bg,
                  border: `1px solid ${blueColors.border}`
                }}
              >
                <Info
                  className="h-4 w-4 shrink-0 mt-0.5"
                  style={{ color: blueColors.icon }}
                />
                <div>
                  <Text
                    as="span"
                    size="xs"
                    weight="semibold"
                    style={{ color: blueColors.title }}
                  >
                    {t('realtime-token-usage')}
                  </Text>
                  <Text
                    as="span"
                    size="xs"
                    className="ml-1"
                    style={{ color: blueColors.text }}
                  >
                    — {t('realtime-token-usage-desc')}
                  </Text>
                  <code
                    className="block px-2 py-1 rounded-2xl mt-1.5 font-mono bg-app-soft"
                    style={{ border: `1px solid ${blueColors.border}` }}
                  >
                    <Text as="span" size="xs">
                      Authorization: Bearer {'{'}your-token{'}'}
                    </Text>
                  </code>
                </div>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Keys table */}
      <div className="w-full px-4 pb-4 relative">
        <DataGridV2
          data={rows}
          columns={columns}
          actions={actions}
          enablePagination
          manualPagination
          totalItems={tokens?.total_results ?? 0}
          enableSorting
          manualSorting
          enableFiltering
          manualFiltering
          pageSize={25}
          pageSizeOptions={[25, 50, 75, 100]}
          showColumnFilters={false}
          filterMode="popover"
          height="84vh"
          dense
          grid={false}
          striped
          loading={isFetching || isLoading}
          getRowId={row => String(row.id)}
        />

        {/* Empty state CTA — only when no filter is active */}
        {rows.length === 0 && !isLoading && !isFetching && !qString && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 pointer-events-none">
            <div className="pointer-events-auto flex flex-col items-center gap-3 mt-8">
              <Text as="p" size="sm" tone="muted">
                {t('no-api-keys-yet')}
              </Text>
              <Button
                label={t('create-first-api-key')}
                icon={<Plus width={14} />}
                onClick={() => setIsModalOpen(true)}
                variant="primary"
                size="small"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
